import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Package, Clock, Bookmark, BookmarkCheck, GitCompare, ArrowUpRight, Megaphone, Info } from 'lucide-react';
import { VerificationBadge } from './VerificationBadge';
import { TrustScore } from './TrustScore';
import { useGiwahs } from '../context/GiwahsContext';
import { FEATURED_TIERS } from '../data/mockData';
import Img from './Img';

// Sponsored placement supplier card — wider layout used in the
// "Featured Suppliers" strip on Factory Directory. Explicitly labelled
// to keep promotional placements separate from trust-based credibility.
const FeaturedSupplierCard = ({ supplier, index = 0 }) => {
  const { savedSuppliers, compareList, toggleSaved, toggleCompare } = useGiwahs();
  const isSaved = savedSuppliers.includes(supplier.id);
  const isCompared = compareList.includes(supplier.id);
  const tier = FEATURED_TIERS[supplier.featuredTier];

  return (
    <article
      data-testid={`featured-supplier-card-${supplier.id}`}
      className="card-dark card-dark--featured group relative flex flex-col reveal-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Sponsored ribbon */}
      <div className="flex items-center justify-between gap-3 border-b border-hairline bg-[#0F0F0F] px-4 py-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-transparent px-3 py-1 label-overline text-gold" style={{ borderColor: 'rgba(200,169,107,0.55)' }} data-testid={`sponsored-label-${supplier.id}`}>
          <Megaphone size={12} strokeWidth={1.6} />
          Sponsored Placement
        </span>
        {tier && (
          <span className="rounded-full border bg-transparent px-3 py-1 font-mono-data text-[10px] uppercase tracking-wider text-platinum-2" style={{ borderColor: 'rgba(255,255,255,0.18)' }} data-testid={`featured-tier-${supplier.id}`}>
            {tier.label} Tier
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12">
        <Link to={`/supplier/${supplier.slug}`} className="relative block aspect-[4/3] overflow-hidden bg-[#0A0A0A] md:col-span-5 md:aspect-auto">
          <Img
            src={supplier.heroImage}
            alt={`${supplier.name} — featured ${supplier.type} in ${supplier.city}, ${supplier.country}`}
            variant="heroNarrow"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </Link>

        <div className="flex flex-col p-5 md:col-span-7 md:p-7 text-platinum">
          {/* D-080 — verification chips relocated beneath the image, placed at top of card body */}
          <div className="flex flex-wrap gap-1.5 pb-4 border-b border-hairline" data-testid={`featured-card-badges-${supplier.id}`}>
            {supplier.badges.map(b => <VerificationBadge key={b} type={b} />)}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="label-overline text-gold">{supplier.type}</span>
            <div className="flex gap-1.5">
              <button
                onClick={() => toggleSaved(supplier.id)}
                data-testid={`featured-save-${supplier.id}`}
                className={`giwahs-icon-btn ${isSaved ? 'giwahs-icon-active' : ''}`}
                style={{ borderColor: 'rgba(200,169,107,0.55)' }}
                title={isSaved ? 'Saved' : 'Save supplier'}
              >
                {isSaved ? <BookmarkCheck size={13} strokeWidth={1.6} /> : <Bookmark size={13} strokeWidth={1.6} />}
              </button>
              <button
                onClick={() => toggleCompare(supplier.id)}
                data-testid={`featured-compare-${supplier.id}`}
                className={`giwahs-icon-btn ${isCompared ? 'giwahs-icon-active' : ''}`}
                style={{ borderColor: 'rgba(200,169,107,0.55)' }}
                title={isCompared ? 'In comparison' : 'Add to compare'}
              >
                <GitCompare size={13} strokeWidth={1.6} />
              </button>
            </div>
          </div>

          <Link to={`/supplier/${supplier.slug}`} className="mt-3 block">
            <h3 className="font-display text-2xl leading-tight tracking-tight md:text-3xl text-platinum" style={{ fontWeight: 600 }}>
              {supplier.name}
            </h3>
          </Link>
          <p className="mt-2 text-sm leading-relaxed text-platinum-2">{supplier.tagline}</p>

          <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-hairline pt-4 font-mono-data text-xs text-platinum-2 sm:grid-cols-4">
            <div className="flex items-center gap-1.5"><MapPin size={12} strokeWidth={1.5} />{supplier.city}, {supplier.country}</div>
            <div className="flex items-center gap-1.5"><Package size={12} strokeWidth={1.5} />MOQ {supplier.moqMin}+</div>
            <div className="flex items-center gap-1.5"><Clock size={12} strokeWidth={1.5} />{supplier.leadTimeDays}d</div>
            <div className="flex items-center gap-1.5">Resp. {supplier.responseRate}%</div>
          </div>

          <div className="mt-4">
            <div className="label-overline text-platinum-2">Certifications</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {supplier.certifications.slice(0, 5).map(c => (
                <span key={c} className="rounded-full border px-2.5 py-0.5 font-mono-data text-[10px] text-platinum-2" style={{ borderColor: 'rgba(255,255,255,0.16)' }}>{c}</span>
              ))}
            </div>
          </div>

          <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-5">
            <TrustScore score={supplier.trustScore} variant="inline" />
            <Link
              to={`/supplier/${supplier.slug}`}
              data-testid={`featured-view-${supplier.id}`}
              className="btn-giwahs btn-giwahs-outline-gold inline-flex items-center gap-1"
            >
              View profile <ArrowUpRight size={12} strokeWidth={1.5} />
            </Link>
          </div>

          {/* Explicit credibility separation note */}
          <div className="mt-4 flex items-start gap-2 border-t border-hairline pt-3 text-[11px] leading-relaxed text-muted-warm">
            <Info size={12} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <span>
              Sponsored placement is a paid promotional listing. Trust score, verification badges, and certifications are independently assessed and not influenced by sponsorship.
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default FeaturedSupplierCard;
